import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3151';

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

/** Réponse GET /U3151/initLU + logicalUnitCode fusionné côté shell. */
export interface U3151InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
}

/** Ligne POST /U3151/search. */
export interface U3151CustomerTypeSearchRow {
  id: string;
  customerTypeCode?: string;
  active?: boolean;
  customerTypeNameFr?: string;
  customerTypeNameEn?: string;
  version: number;
  deletedAt?: string | Date | null;
  createdAt?: string | Date;
}

export interface U3151CustomerTypeTranslation {
  id?: string;
  languageCode: string;
  customerTypeName: string;
  version?: number;
}

/** Détail GET /U3151/:id (result). */
export interface U3151CustomerType {
  id: string;
  customerTypeCode: string;
  active: boolean;
  translations: U3151CustomerTypeTranslation[];
  version: number;
}

export type U3151CustomerTypeTranslationFormRow = U3151CustomerTypeTranslation;

export type U3151CustomerTypeFormRow = U3151CustomerType;

export interface U3151CustomerTypeCreateBody {
  customerTypeCode: string;
  active: boolean;
  translations: Array<{
    languageCode: string;
    customerTypeName: string;
  }>;
}

export interface U3151CustomerTypeUpdateBody {
  id: string;
  active: boolean;
  version: number;
  translations: Array<
    | {
        languageCode: string;
        customerTypeName: string;
      }
    | {
        id: string;
        languageCode: string;
        customerTypeName: string;
        version: number;
      }
  >;
}

export interface U3151CustomerTypeDeleteBody {
  id: string;
  version: number;
}

export const U3151_ERROR_CODE_DUPLICATE = 'BU315101';
export const U3151_ERROR_CODE_IN_USE = 'BU315102';

export function createEmptyU3151FormRow(): U3151CustomerTypeFormRow {
  return {
    id: '',
    customerTypeCode: '',
    active: true,
    version: 0,
    translations: [
      { languageCode: 'fr', customerTypeName: '' },
      { languageCode: 'en', customerTypeName: '' },
    ],
  };
}

export function normalizeU3151FromApi(row: U3151CustomerType): U3151CustomerType {
  return {
    ...row,
    active: row.active !== false,
    translations: Array.isArray(row.translations)
      ? row.translations.map((tr) => ({
          ...tr,
          languageCode: typeof tr.languageCode === 'string' ? tr.languageCode.trim().toLowerCase() : tr.languageCode,
        }))
      : [],
  };
}

function u3151TranslationDescription(
  translations: U3151CustomerTypeTranslation[] | undefined,
  lang: 'fr' | 'en',
): string | undefined {
  if (!Array.isArray(translations)) return undefined;
  const tr = translations.find((t) => t.languageCode?.trim().toLowerCase() === lang);
  const desc = tr?.customerTypeName?.trim();
  return desc || undefined;
}

/** Reconstruit une ligne search (descriptions FR/EN) à partir du détail fiche — évite la perte d'affichage liste après GET/save. */
export function u3151FormRowToSearchRow(
  row: U3151CustomerType | U3151CustomerTypeFormRow,
  existing?: U3151CustomerTypeSearchRow | null,
): U3151CustomerTypeSearchRow {
  const searchRow: U3151CustomerTypeSearchRow = {
    id: row.id,
    customerTypeCode: row.customerTypeCode,
    active: row.active !== false,
    customerTypeNameFr: u3151TranslationDescription(row.translations, 'fr'),
    customerTypeNameEn: u3151TranslationDescription(row.translations, 'en'),
    version: row.version,
  };
  if (existing) {
    return { ...existing, ...searchRow };
  }
  return searchRow;
}

export function u3151FormatCustomerTypeListLabel(row: U3151CustomerTypeSearchRow, language: string): string {
  const code = row.customerTypeCode?.trim() ?? '';
  const isFr = language.startsWith('fr');
  const linguistic =
    (isFr ? row.customerTypeNameFr : row.customerTypeNameEn)?.trim() ||
    row.customerTypeNameFr?.trim() ||
    row.customerTypeNameEn?.trim() ||
    '';
  if (code && linguistic) return `${code} — ${linguistic}`;
  return code || linguistic;
}

export function buildCreateBody(data: U3151CustomerTypeFormRow): U3151CustomerTypeCreateBody {
  return {
    customerTypeCode: data.customerTypeCode.trim(),
    active: data.active !== false,
    translations: data.translations.map((tr) => ({
      languageCode: tr.languageCode.trim().toLowerCase(),
      customerTypeName: tr.customerTypeName.trim(),
    })),
  };
}

export function buildUpdateBody(data: U3151CustomerTypeFormRow): U3151CustomerTypeUpdateBody {
  return {
    id: data.id,
    active: data.active !== false,
    version: data.version,
    translations: data.translations.map((tr) => {
      const languageCode = tr.languageCode.trim().toLowerCase();
      const customerTypeName = tr.customerTypeName.trim();
      const hasId = Boolean(tr.id && String(tr.id).trim() !== '');
      if (hasId && tr.id) {
        return {
          id: tr.id,
          languageCode,
          customerTypeName,
          version: typeof tr.version === 'number' ? tr.version : 0,
        };
      }
      return { languageCode, customerTypeName };
    }),
  };
}

export function buildDeleteBody(data: U3151CustomerTypeFormRow): U3151CustomerTypeDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3151CustomerTypeCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3151CustomerTypeUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3151CustomerTypeDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3151_ERROR_CODE_DUPLICATE:
      errorResult.fieldErrors['customerTypeCode'] = t('errCustomerTypeCodeDuplicate', { ns: nameSpace });
      break;
    case U3151_ERROR_CODE_IN_USE:
      errorResult.genericErrors.push({
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errDeleteInUse', { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
